import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';

import {
    Flex,
    Spacer,
    TextButton,
} from '@zillow/constellation';
import { Row, Col } from '@zillow/zrs-comet';

import { STEP } from 'app/shared/containers/ListingClaimPage/constants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import { PHONE_VERIFICATION_STEPS } from 'app/shared/constants/WizardStepsConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import ListingClaimUtils from 'app/shared/utils/listingClaimUtils';

import appLogger from 'app/shared/loggers/appLogger';
import { PhoneVerificationWizard } from 'app/shared/components/Wizard/PhoneVerificationWizard';
import { RMW_PATH } from 'app/shared/pathsManifest';
import ClaimActions from 'app/shared/actions/ClaimActions';
import ContactMethodDetail from 'app/shared/components/WizardListingClaim/ContactMethodDetail';
import EmailVerificationActions from 'app/shared/actions/EmailVerificationActions';
import ManageClaimContactMethodsModal from 'app/shared/components/Modal/ManageClaimContactMethodsModal';
import ViewClaimListingsModal from 'app/shared/components/Modal/ViewClaimListingsModal';
import ListingRemovalModal from 'app/shared/components/Modal/ListingRemovalModal';
import WebSocketMessageNameConstants from 'app/shared/constants/WebSocketMessageNameConstants';
import WizardActionFooter from 'app/shared/components/WizardActionFooter';
import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';

import {
    StyledVerticalDivider,
    StyledContactMethodContainer,
} from './StyledComponents';

const {
    combineContactMethods,
    getContactType,
    getVerifiedContacts,
} = ListingClaimUtils;

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
    onGoToNextStep: PropTypes.func.isRequired,
};

const phoneVerificationConfig = [
    PHONE_VERIFICATION_STEPS.GET_VERIFICATION_CODE,
    PHONE_VERIFICATION_STEPS.VERIFICATION_CODE_SENT,
    PHONE_VERIFICATION_STEPS.VERIFICATION_SUCCESS,
];

const WizVerifyContactList = ({
    onGoToNextStep,
    onGoToPrevStep,
}) => {
    const dispatch = useDispatch();
    const [listings, setListings] = useState([]);
    const [unverifiedContactMethods, setUnverifiedContactMethods] = useState([]);
    const contactMethods = useSelector(state => state.claimListings.claimMethods);
    const [isTransitioning, setIsTransitioning] = useState(true);
    const hasError = useSelector(state => state.claimListings.claimMethods.loadingState) === LoadingConstants.ERROR;
    const [phoneToVerify, setPhoneToVerify] = useState(null);
    const [showManageContactMethodsModal, setShowManageContactMethodsModal] = useState(false);
    const [showListingRemovalModal, setShowListingRemovalModal] = useState(false);
    const [showPhoneVerificationWizard, setShowPhoneVerificationWizard] = useState(false);
    const [showViewListingsModal, setShowViewListingsModal] = useState(false);
    const [pendingEmails, setPendingEmails] = useState([]);
    const contactMethodsList = combineContactMethods(contactMethods);

    const handleWebSocketMessage = (message) => {
        let data;
        try {
            data = JSON.parse(message.data);
        } catch (error) {
            return;
        }
        if (data.name !== WebSocketMessageNameConstants.EMAIL_VERIFIED) {
            return;
        }

        const indexOfVerifiedEmail = contactMethods.emails
            .findIndex(contactObj => contactObj.value === data.payload.email);
        const indexOfPendingEmail = pendingEmails.findIndex(pendingEmail => pendingEmail === data.payload.email);

        if (indexOfVerifiedEmail >= 0) {
            const emailsCopy = cloneDeep(contactMethods.emails);
            const pendingEmailsCopy = [...pendingEmails];

            emailsCopy[indexOfVerifiedEmail].isVerified = true;
            pendingEmailsCopy.splice(indexOfPendingEmail, 1);

            setPendingEmails(pendingEmailsCopy);
            dispatch(ClaimActions.setClaimMethods({ emails: emailsCopy }));
        }
    };

    useEffect(() => {
        setIsTransitioning(true);
        window.rentalManagerApiWebSocket.addEventListener('message', handleWebSocketMessage);
        dispatch(ClaimActions.getClaimMethods()).then(() => setIsTransitioning(false));

        return () => window.rentalManagerApiWebSocket.removeEventListener('message', handleWebSocketMessage);
    }, []);

    const handlePhoneVerificationComplete = (verifiedPhoneNumber) => {
        const indexOfVerifiedPhone = contactMethods.phones
            .findIndex(contactObj => contactObj.value === verifiedPhoneNumber);

        if (indexOfVerifiedPhone >= 0) {
            const phonesCopy = cloneDeep(contactMethods.phones);
            phonesCopy[indexOfVerifiedPhone].isVerified = true;

            dispatch(ClaimActions.setClaimMethods({ phones: phonesCopy }));
            return Promise.resolve();
        }

        return Promise.reject(new Error('There was an error. Please try again.'));
    };

    const handleOnVerifyPhoneClick = (phoneNumber) => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION,
                action: 'ClicksPhoneVerifyNow',
            },
        ));
        setShowPhoneVerificationWizard(true);
        setPhoneToVerify(phoneNumber);
    };

    const handleOnVerifyEmailClick = (email) => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION,
                action: 'ClicksSendVerificationEmail',
            },
        ));
        dispatch(EmailVerificationActions.sendSecurityToken({
            emailVerificationType: 'claimFeedListings',
            successRedirectPath: `${RMW_PATH.LISTING_CLAIMING}?step=${STEP.VERIFY_CONTACT_LIST}`,
            email,
        }))
            .then((response) => {
                if (response.success) {
                    setPendingEmails([...pendingEmails, email]);
                }
            })
            .catch((err) => {
                appLogger.logError({
                    message: err,
                    context: 'claimContactMethods.emailVerification',
                });
            });
    };

    const handleManageContact = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION,
                action: 'ManageContactMethod',
            },
        ));
        setShowManageContactMethodsModal(true);
    };

    const handleViewListings = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION,
                action: 'ViewListings',
            },
        ));
        setShowViewListingsModal(true);
    };

    const handleLearnMoreClick = () => {
        window.open(RMW_PATH.LISTING_CLAIMING_HELP, '_blank');
    };

    const handleClickPrevStep = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_BACK,
            { label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION },
        ));
        onGoToPrevStep();
    };

    const handleNextClick = async () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_NEXT,
            { label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.CONTACT_VERIFICATION },
        ));
        const {
            contactMethods: newUnverifiedContactMethods,
            listings: unverifiedListings,
        } = await dispatch(ClaimActions.getExclusivePropertiesForAllUnverified());

        if (unverifiedListings.length > 0) {
            setListings(unverifiedListings);
            setUnverifiedContactMethods(newUnverifiedContactMethods);
            setShowListingRemovalModal(true);
        } else {
            onGoToNextStep();
        }
    };

    const getEmailVerificationText = (email) => {
        const emailsAction = pendingEmails.includes(email) ? 'Resend' : 'Send';
        return `${emailsAction} verification email`;
    };

    return (
        <WizardBodyTemplate
            title="Contact Verification"
            tooltipText="Trouble with verification? Check out our help article for more information."
            // tooltipHelpText="See help"
            isTransitioning={isTransitioning}
            hasError={hasError}
            onTooltipHelpClick={handleLearnMoreClick}
        >
            <Spacer marginBottom="sm">
                <Flex display="flex" justifyContent="center" alignItems="center">
                    <TextButton onClick={handleManageContact}>
                        Manage contact methods
                    </TextButton>
                    <StyledVerticalDivider />
                    <TextButton onClick={handleViewListings}>View listings</TextButton>
                </Flex>
            </Spacer>
            <Row>
                <Col xs={0} md={2} />
                <Col xs={12} md={8}>
                    <StyledContactMethodContainer>
                        {contactMethodsList.map((contactMethod, i) => (
                            <ContactMethodDetail
                                emailVerificationText={getEmailVerificationText(contactMethod.value)}
                                key={`contactMethodDetail-${i}`}
                                type={getContactType(contactMethod)}
                                data={contactMethod}
                                onVerifyPhoneClick={handleOnVerifyPhoneClick}
                                onVerifyEmailClick={handleOnVerifyEmailClick}
                            />
                        ))}
                    </StyledContactMethodContainer>
                </Col>
                <Col xs={0} md={2} />
            </Row>
            <WizardActionFooter
                onBackClick={handleClickPrevStep}
                onNextClick={handleNextClick}
                isBackDisabled={!onGoToPrevStep}
                isNextDisabled={!getVerifiedContacts(contactMethodsList)}
            />
            {showManageContactMethodsModal && (
                <ManageClaimContactMethodsModal
                    onClose={() => setShowManageContactMethodsModal(false)}
                    contactMethods={contactMethods}
                />
            )}
            {showListingRemovalModal && (
                <ListingRemovalModal
                    onClose={() => setShowListingRemovalModal(false)}
                    onSubmitSuccess={onGoToNextStep}
                    listings={listings}
                    contactMethods={unverifiedContactMethods}
                />
            )}
            {showPhoneVerificationWizard && (
                <PhoneVerificationWizard
                    headerText="Verify phone number"
                    isNumberEditable={false}
                    showVerificationModal={setShowPhoneVerificationWizard}
                    stepsConfig={phoneVerificationConfig}
                    numberToVerify={phoneToVerify}
                    verificationSuccessfulCTAText="Return to contact verification"
                    onVerificationConfirmation={handlePhoneVerificationComplete}
                />
            )}
            {showViewListingsModal && (
                <ViewClaimListingsModal
                    onClose={() => setShowViewListingsModal(false)}
                />
            )}
        </WizardBodyTemplate>
    );
};

WizVerifyContactList.propTypes = propTypes;
WizVerifyContactList.defaultProps = {};

export default WizVerifyContactList;
